/**
 * useLayoverTitle — the header-center page title for layover surfaces.
 *
 * Layover surfaces (settings + products) hide the ModeSpine and the derived
 * `headerTitle`, leaving the header center empty. The AppShell fills it with the
 * CURRENT page's own name. Rather than duplicate every page's title in a map in
 * AppShell (which would drift from each page's real wording and miss dynamic
 * titles like a member's name on the Member-detail sub-page), each layover page
 * PUBLISHES its title here while it is mounted and AppShell READS it.
 *
 * This keeps one source per title: the same string a settings page already
 * passes to `SettingsLayover` (e.g. "Billing & subscriptions", "App settings",
 * the member's name) is what the header shows; the products layover publishes its
 * own "Household products". Adding a future layover means publishing from that
 * page — no AppShell change.
 *
 * A standalone Zustand store (not the root store) because this is transient
 * view-only chrome state with no reducer/persistence/orchestrator wiring.
 */

import { useEffect } from 'react';
import { create } from 'zustand';

interface LayoverTitleState {
  title: string | null;
}

const useLayoverTitleStore = create<LayoverTitleState>(() => ({ title: null }));

/** Read the current layover page title (null when no layover page is mounted). */
export function useLayoverTitle(): string | null {
  return useLayoverTitleStore((s) => s.title);
}

/**
 * Publish `title` as the layover header title for as long as the calling page is
 * mounted, clearing it on unmount. Call once per layover page with the same
 * string the page already displays as its own title, so the header label always
 * matches the page.
 *
 * A null / undefined / empty title is NOT published — the page can call this
 * unconditionally (hooks must run before any early return) while its title is
 * still loading; the AppShell keeps its "Settings" fallback until a real title
 * arrives, never flashing an empty header.
 */
export function usePublishLayoverTitle(title: string | null | undefined): void {
  const publishable = title != null && title.trim() !== '' ? title : null;
  useEffect(() => {
    if (publishable == null) return;
    useLayoverTitleStore.setState({ title: publishable });
    return () => {
      // Only clear if we are still the published title — guards against a fast
      // route swap where the next page publishes before this one's cleanup runs.
      if (useLayoverTitleStore.getState().title === publishable) {
        useLayoverTitleStore.setState({ title: null });
      }
    };
  }, [publishable]);
}
